/**
 * cloud-pos table-visualization API (Partners Admin).
 * Collection spelling: visualization (not visualisation).
 *
 * Tenant scope is provided by the global `business: <business-id>` header that
 * `services/api.js` sets on the axios `instance` (see setInstance). The gateway
 * scopes table-visualization by that header, so no per-request business header
 * is sent here.
 *
 * Endpoints (gateway /api01 base is already on REACT_APP_API_BASE_URL):
 *   GET    /cloud-pos/table-visualization
 *   GET    /cloud-pos/table-visualization/current
 *   POST   /cloud-pos/table-visualization
 *   PATCH  /cloud-pos/table-visualization
 *   DELETE /cloud-pos/table-visualization
 */

import instance from 'services/api';

const PATH = '/cloud-pos/table-visualization';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const unwrap = (response) => {
  const body = response?.data;
  if (body == null) {
    return null;
  }
  // Standard partners envelope: { data: doc | docs, message? }
  if (Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data;
  }
  return body;
};

/** GET list of floor-plan documents for the business. */
export const listTableVisualizations = async () => {
  const response = await instance.get(PATH);
  const data = unwrap(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

/** GET current floor-plan document for the business (one per business). */
export const getCurrentTableVisualization = async () => {
  const response = await instance.get(`${PATH}/current`);
  return unwrap(response);
};

/** POST create floor plan. */
export const createTableVisualization = async ({
  version = 1,
  sections = {},
} = {}) => {
  const response = await instance.post(
    PATH,
    { version, sections },
    { headers: JSON_HEADERS }
  );
  return unwrap(response);
};

/** PATCH update current floor plan (last-write-wins whole document). */
export const updateTableVisualization = async ({
  version = 1,
  sections = {},
} = {}) => {
  const response = await instance.patch(
    PATH,
    { version, sections },
    { headers: JSON_HEADERS }
  );
  return unwrap(response);
};

/** DELETE current floor plan. */
export const deleteTableVisualization = async () => {
  const response = await instance.delete(PATH);
  return unwrap(response);
};

/**
 * Save the floor plan with a single PATCH (last-write-wins upsert), matching
 * the gateway contract. The backend creates the document on first PATCH, so no
 * GET/POST bootstrap is needed. Returns `{ doc, created }`.
 */
export const upsertTableVisualization = async (plan) => {
  const version =
    typeof plan?.version === 'number' && Number.isFinite(plan.version)
      ? plan.version
      : 1;
  const sections = plan?.sections || {};

  const doc = await updateTableVisualization({ version, sections });
  return { doc, created: false };
};
